import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SubmissionsTable from './components/SubmissionsTable';
import FilterPanel from './components/FilterPanel';
import BulkActionsBar from './components/BulkActionsBar';
import LabelManagementModal from './components/LabelManagementModal';
import SubmissionDetailModal from './components/SubmissionDetailModal';
import { submissionsManagementService } from '../../services/submissionsManagementService';
import { useAuth } from '../../contexts/AuthContext';

const SubmissionsManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [submissions, setSubmissions] = useState([]);
  const [forms, setForms] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    formId: null,
    status: null,
    dateFrom: null,
    dateTo: null,
    labelId: null,
    searchTerm: ''
  });
  
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/authentication');
      return;
    }

    loadData();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;

    // Subscribe to real-time updates
    const unsubscribe = submissionsManagementService?.subscribeToAllSubmissions(
      user?.id,
      (payload) => {
        if (payload?.eventType === 'INSERT') {
          loadData(); // Reload to get full data with joins
        } else if (payload?.eventType === 'UPDATE') {
          setSubmissions(prev => 
            prev?.map(s => s?.id === payload?.new?.id ? { ...s, ...payload?.new } : s)
          );
        } else if (payload?.eventType === 'DELETE') {
          setSubmissions(prev => prev?.filter(s => s?.id !== payload?.old?.id));
        }
      }
    );

    return () => unsubscribe();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [submissionsData, formsData, labelsData] = await Promise.all([
        submissionsManagementService?.getAllSubmissions(filters),
        submissionsManagementService?.getUserForms(),
        submissionsManagementService?.getLabels()
      ]);
      
      setSubmissions(submissionsData);
      setForms(formsData);
      setLabels(labelsData);
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError('Failed to load submissions data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (newFilters) => {
    setFilters(newFilters);
    try {
      const submissionsData = await submissionsManagementService?.getAllSubmissions(newFilters);
      setSubmissions(submissionsData);
    } catch (err) {
      console.error('Error applying filters:', err);
    }
  };

  const handleExport = () => {
    const dataToExport = selectedSubmissions?.length > 0
      ? submissions?.filter(s => selectedSubmissions?.includes(s?.id))
      : submissions;
    
    submissionsManagementService?.exportToCSV(dataToExport);
  };

  const handleBulkStatusUpdate = async (status) => {
    try {
      await submissionsManagementService?.bulkUpdateStatus(selectedSubmissions, status);
      setSelectedSubmissions([]);
      loadData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleBulkLabelAssign = async (labelId) => {
    try {
      await submissionsManagementService?.bulkAssignLabel(selectedSubmissions, labelId);
      setSelectedSubmissions([]);
      loadData();
    } catch (err) {
      console.error('Error assigning label:', err);
    }
  };

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);
  };

  const handleCreateLabel = async (name, color) => {
    try {
      await submissionsManagementService?.createLabel(name, color);
      const labelsData = await submissionsManagementService?.getLabels();
      setLabels(labelsData);
    } catch (err) {
      console.error('Error creating label:', err);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar>
          <div className="flex items-center justify-center h-[calc(100vh-64px)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading submissions...</p>
            </div>
          </div>
        </NavigationBar>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar>
          <div className="flex items-center justify-center h-[calc(100vh-64px)]">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <p className="text-foreground font-semibold mb-2">Error Loading Data</p>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={loadData}>Try Again</Button>
            </div>
          </div>
        </NavigationBar>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar>
      
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Submissions Management
              </h1>
              <p className="text-muted-foreground mt-1">
                View, organize, and manage all form submissions in one place
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Tag"
                iconPosition="left"
                onClick={() => setShowLabelModal(true)}
              >
                Manage Labels
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                onClick={handleExport}
              >
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Submissions</p>
                <p className="text-2xl font-bold text-foreground mt-1">{submissions?.length}</p>
              </div>
              <div className="bg-primary/10 p-3 rounded-lg">
                <Icon name="FileText" size={24} className="text-primary" />
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">New</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {submissions?.filter(s => s?.status === 'new')?.length}
                </p>
              </div>
              <div className="bg-blue-500/10 p-3 rounded-lg">
                <Icon name="Inbox" size={24} className="text-blue-500" />
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Reviewed</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {submissions?.filter(s => s?.status === 'reviewed')?.length}
                </p>
              </div>
              <div className="bg-success/10 p-3 rounded-lg">
                <Icon name="CheckCircle2" size={24} className="text-success" />
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Archived</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {submissions?.filter(s => s?.status === 'archived')?.length}
                </p>
              </div>
              <div className="bg-muted p-3 rounded-lg">
                <Icon name="Archive" size={24} className="text-muted-foreground" />
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <FilterPanel
          filters={filters}
          forms={forms}
          labels={labels}
          onFilterChange={handleFilterChange}
        />

        {/* Bulk Actions Bar */}
        {selectedSubmissions?.length > 0 && (
          <BulkActionsBar
            selectedCount={selectedSubmissions?.length}
            labels={labels}
            onStatusUpdate={handleBulkStatusUpdate}
            onLabelAssign={handleBulkLabelAssign}
            onClearSelection={() => setSelectedSubmissions([])}
          />
        )}

        {/* Submissions Table */}
        <SubmissionsTable
          submissions={submissions}
          labels={labels}
          selectedSubmissions={selectedSubmissions}
          onSelectionChange={setSelectedSubmissions}
          onViewDetails={handleViewDetails}
          searchTerm={filters?.searchTerm}
        />
      </div>

      {/* Modals */}
      {showLabelModal && (
        <LabelManagementModal
          labels={labels}
          onClose={() => setShowLabelModal(false)}
          onCreateLabel={handleCreateLabel}
        />
      )}

      {showDetailModal && selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          labels={labels}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedSubmission(null);
          }}
          onLabelChange={loadData}
        />
      )}
      </NavigationBar>
    </div>
  );
};

export default SubmissionsManagement;